import { useInfiniteQuery } from '@tanstack/react-query';

import { parseDateRange } from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { FilterValues } from '@/hooks/use-filter-values';
import { flattenInfinitePages, getNextPageParam } from '@/lib/constants/pagination';
import { formatDateToISO, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getOverheads } from '../api';
import { OVERHEADS_QUERY_KEY, OVERHEADS_STALE_TIME } from '../constants/overheads-query-keys';
import { GetOverheadsFilter, OverheadStatus, OverheadType } from '../types/overhead';

export const useOverheads = ({ filters }: { filters: FilterValues['values'] }) => {
  return useInfiniteQuery({
    queryKey: [OVERHEADS_QUERY_KEY, filters],
    queryFn: ({ pageParam }) =>
      getOverheads({ cursor: pageParam, filters: mapOverheadsFilters(filters) }),
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    staleTime: OVERHEADS_STALE_TIME,
    select: flattenInfinitePages,
  });
};

/**
 * Maps UI filter values to the overheads API filter shape.
 */
export const mapOverheadsFilters = (filters: FilterValues['values']): GetOverheadsFilter => {
  const apiFilters: GetOverheadsFilter = {};

  const dates = parseDateRange(typeof filters.period === 'string' ? filters.period : '');
  if (dates) {
    apiFilters.dateFrom = formatDateToISO(normalizeToStartOfDayUTC(dates.startDate));
    apiFilters.dateTo = formatDateToISO(normalizeToStartOfDayUTC(dates.endDate));
  }

  if (filters.searchString && typeof filters.searchString === 'string') {
    apiFilters.searchString = filters.searchString;
  }

  if (filters.type) {
    const typeArray = Array.isArray(filters.type) ? filters.type : [filters.type];
    apiFilters.types = typeArray as OverheadType[];
  }

  if (filters.status) {
    const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status];
    apiFilters.statuses = statusArray as OverheadStatus[];
  }

  if (filters.project) {
    const projectArray = Array.isArray(filters.project) ? filters.project : [filters.project];
    apiFilters.projectIds = projectArray.map((id: string | number) => Number(id));
  }

  if (filters.client) {
    const clientArray = Array.isArray(filters.client) ? filters.client : [filters.client];
    apiFilters.clientIds = clientArray.map((id: string | number) => Number(id));
  }

  return apiFilters;
};
